import { deleteDB, openDB, type DBSchema, type IDBPDatabase } from "idb";

import { OCTI_WEB_CHANNEL } from "../version";

/**
 * Stored credential for one linked sync-connector instance. Today the only
 * connector type is the Octi sync server (`kserver` on the wire, matching the
 * Android `ConnectorType.OCTISERVER.typeId`). A future GDriveCredentialRecord
 * (or similar) joins this union with its own `connectorType` discriminator —
 * the IndexedDB store key (`connectorId`) stays the same shape.
 *
 * v1 of the UI only ever holds a single record at a time, enforced by link /
 * create flows calling {@link CredentialsRepo.replaceAllWith}. The schema +
 * multi-record APIs (`listAll`, etc.) are already shaped for multi-connector
 * so the eventual switch doesn't require a migration.
 */
export type CredentialRecord = OctiServerCredentialRecord;

export interface OctiServerCredentialRecord {
  /**
   * Connector-instance primary key. Format: `kserver-<domain>-<accountId>`,
   * mirroring Android's `ConnectorId.idString`. Stable across renames and
   * across pre-launch restarts of the same account.
   */
  connectorId: string;
  /** Discriminator. Matches Android's `ConnectorType.typeId` wire string. */
  connectorType: "kserver";
  /** Server-assigned UUID. Still used as the username half of HTTP Basic auth. */
  accountId: string;
  /** Device-scoped password generated by the server on POST /v1/account (~50 chars). */
  devicePassword: string;
  /** UUID we generated and sent as X-Device-ID at registration. */
  ownDeviceId: string;
  /** User-supplied label shown to other devices (e.g. "Firefox @ Desktop"). */
  deviceLabel: string;
  /** Sync server endpoint. Reconstructed URL = `${protocol}://${domain}:${port}`. */
  serverAddress: {
    domain: string;
    protocol: "http" | "https";
    port: number;
  };
  /**
   * Tink proto keyset bytes (typically ~70 bytes) — the AES-GCM-SIV key shared
   * across all devices on the account.
   */
  encryptionKeyset: Uint8Array;
  /** When this record was first created locally (epoch ms). Never changes after save. */
  createdAt: number;
  /**
   * Bumped on every {@link CredentialsRepo.save}. Drives {@link CredentialsRepo.getActive}
   * ordering — `createdAt` doesn't move on rename, so it can't represent
   * "most recently used".
   */
  updatedAt: number;
}

interface OctiWebDB extends DBSchema {
  credentials: {
    key: string; // connectorId
    value: CredentialRecord;
  };
}

/**
 * Channel-scoped storage. Canary and stable share the same origin
 * ({@code web.octi.darken.eu}), so suffixing the DB name isolates their
 * schemas. Channels are independent devices on the sync account; pairing both
 * gives the user two device records on the same account.
 */
const DB_NAME = `octi-web-${OCTI_WEB_CHANNEL}`;
const DB_VERSION = 2;
const STORE = "credentials";

let dbPromise: Promise<IDBPDatabase<OctiWebDB>> | null = null;

function getDb(): Promise<IDBPDatabase<OctiWebDB>> {
  if (!dbPromise) {
    dbPromise = openDB<OctiWebDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // v2 schema-of-record: keyPath = "connectorId". Handles fresh installs
        // (no stores yet) and any pre-launch v1 carriers (delete + recreate is
        // safe because v1 never shipped a real user).
        if (db.objectStoreNames.contains(STORE)) {
          db.deleteObjectStore(STORE);
        }
        db.createObjectStore(STORE, { keyPath: "connectorId" });
      },
    });
  }
  return dbPromise;
}

export class CredentialsRepo {
  async save(record: CredentialRecord): Promise<void> {
    const db = await getDb();
    await db.put(STORE, { ...record, updatedAt: Date.now() });
  }

  /**
   * Replace the entire store with `record` in a single transaction. Used by
   * link/create flows so a failed write can't leave the user with no local
   * credential after the sync-server has already registered the device.
   * Atomic: either the new record is fully present or the old one is.
   */
  async replaceAllWith(record: CredentialRecord): Promise<void> {
    const db = await getDb();
    const tx = db.transaction(STORE, "readwrite");
    await tx.objectStore(STORE).clear();
    await tx.objectStore(STORE).put({ ...record, updatedAt: Date.now() });
    await tx.done;
  }

  /**
   * Single-connector-UX accessor: returns the record most-recently touched
   * by {@link CredentialsRepo.save} / {@link CredentialsRepo.replaceAllWith},
   * tie-breaking on `connectorId` for determinism. Multi-connector code
   * should call {@link CredentialsRepo.listAll} instead.
   */
  async getActive(): Promise<CredentialRecord | undefined> {
    const all = await this.listAll();
    if (all.length === 0) return undefined;
    return all.reduce((best, cur) => {
      if (cur.updatedAt > best.updatedAt) return cur;
      if (cur.updatedAt < best.updatedAt) return best;
      return cur.connectorId < best.connectorId ? cur : best;
    });
  }

  async listAll(): Promise<CredentialRecord[]> {
    const db = await getDb();
    return db.getAll(STORE);
  }

  /**
   * Remove a single credential by its connector id. Used by
   * {@code ConnectorManager.removeConnector} so the user can disconnect one
   * sync source without wiping the others.
   */
  async deleteByConnectorId(connectorId: string): Promise<void> {
    const db = await getDb();
    await db.delete(STORE, connectorId);
  }

  /** Clears the store. Used by Settings "sign out from this device". */
  async wipe(): Promise<void> {
    const db = await getDb();
    await db.clear(STORE);
  }

  /**
   * Closes the open connection (if any) and drops the whole IndexedDB
   * database. Used by sign-out and by tests to reset the module-level
   * singleton between cases.
   *
   * Uses `idb`'s {@link deleteDB} rather than `indexedDB.deleteDatabase()`
   * — the latter returns an `IDBOpenDBRequest`, not a Promise, so awaiting it
   * is a no-op and the caller would race past while deletion is still in
   * flight (and blocked/error events would be silently swallowed).
   */
  async wipeAll(): Promise<void> {
    if (dbPromise) {
      const db = await dbPromise;
      db.close();
      dbPromise = null;
    }
    await deleteDB(DB_NAME);
  }
}

export const credentialsRepo = new CredentialsRepo();
